import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import {
  User, Mail, Lock, ChevronRight, ChevronLeft, Check, X,
  Code2, Server, LineChart, Loader2, Eye, EyeOff, ArrowLeft, MailCheck, MailWarning
} from 'lucide-react'
import CaptchaCheckbox from '../components/CaptchaCheckbox'
import { isOurApp } from '../lib/platform'
import { FIELD as SHARED_FIELD } from '../components/dashboard/styles'

const PROGRAMS = [
  { value: 'BSCS', label: 'BS Computer Science', icon: Code2, description: 'Software Development & Algorithms' },
  { value: 'BSIT', label: 'BS Information Technology', icon: Server, description: 'IT Infrastructure & Systems' },
  { value: 'BSIS', label: 'BS Information Systems', icon: LineChart, description: 'Business Systems & Analytics' },
]

/**
 * Year level, as the ordinal it is.
 *
 * This used to be a growing plant — Sprout, Flower2, TreeDeciduous,
 * GraduationCap — each in its own purple gradient slab. Two problems with that.
 * A seedling does not mean "first year" to anybody who has not been told, so the
 * label was carrying the meaning and the icon was carrying decoration; and §11
 * is explicit that a choice card is a dark card with an icon chip, never a
 * coloured slab (§15 bans the drop-shadow that came with it).
 *
 * "1st" is the shortest true answer, so that is what the tile shows.
 */
const YEAR_LEVELS = [
  { value: '1', ordinal: '1st', label: '1st Year' },
  { value: '2', ordinal: '2nd', label: '2nd Year' },
  { value: '3', ordinal: '3rd', label: '3rd Year' },
  { value: '4', ordinal: '4th', label: '4th Year' },
]

/** What each step is for, so the strip can say rather than just count. */
const STEP_NAMES = ['Your name', 'Course details', 'Password']

/** One definition of a text field, so eight of them cannot drift apart.
 *  `rounded-lg` (controls, not cards), a solid surface, a real focus ring —
 *  `focus:outline-none` with only a border tint leaves keyboard users guessing
 *  — and `min-h-11` for the touch minimum. */
// Shared, because this string had drifted into four files.
const FIELD = SHARED_FIELD

export default function Register() {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    program: '',
    year_level: '',
    password: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  // Step 5: awaiting email confirmation after signup
  const [registeredEmail, setRegisteredEmail] = useState('')
  const [emailSent, setEmailSent] = useState(true)
  const [resending, setResending] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaAnswer, setCaptchaAnswer] = useState<number | null>(null)
  const navigate = useNavigate()
  const { setAuthData } = useAuth()

  const handleCaptchaVerified = (token: string, answer: number) => {
    setCaptchaToken(token)
    setCaptchaAnswer(answer)
  }

  const handleCaptchaExpired = () => {
    setCaptchaToken('')
    setCaptchaAnswer(null)
  }

  const validateStep = () => {
    if (step === 1) {
      if (!formData.first_name.trim()) {
        toast.error('Please enter your first name')
        return false
      }
      if (!formData.last_name.trim()) {
        toast.error('Please enter your last name')
        return false
      }
      if (!formData.username.trim()) {
        toast.error('Please choose a username')
        return false
      }
    } else if (step === 2) {
      if (!formData.email.trim()) {
        toast.error('Please enter your email')
        return false
      }
      if (!formData.email.endsWith('@ssct.edu.ph') && !formData.email.endsWith('@snsu.edu.ph')) {
        toast.error('Please use your institutional email (@ssct.edu.ph)')
        return false
      }
      if (!formData.program) {
        toast.error('Please select your program')
        return false
      }
      if (!formData.year_level) {
        toast.error('Please select your year level')
        return false
      }
    } else if (step === 3) {
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters')
        return false
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return false
      }
      if (!captchaToken || captchaAnswer === null) {
        toast.error('Please complete the CAPTCHA verification')
        return false
      }
    }
    return true
  }

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  const handleResendVerification = async () => {
    if (!registeredEmail) return
    setResending(true)
    try {
      await authAPI.resendVerification(registeredEmail)
      setEmailSent(true)
      toast.success('Confirmation email sent — check your inbox.')
    } catch {
      toast.error('Could not send the email right now. Please try again shortly.')
    } finally {
      setResending(false)
    }
  }

  const handleSubmit = async () => {
    if (!validateStep()) return

    setLoading(true)
    try {
      const response = await authAPI.register({
        email: formData.email,
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        program: formData.program,
        year_level: formData.year_level,
        role: 'student',
        captcha_token: captchaToken,
        captcha_answer: captchaAnswer,
      })

      if (response.data.tokens) {
        // Legacy path: server issued tokens directly (verification disabled)
        setAuthData(response.data.tokens.access, response.data.user, response.data.tokens.refresh)
        setStep(4) // Success step
        setTimeout(() => {
          navigate('/learning')
        }, 2000)
      } else if (response.data.verification_required) {
        // Account created but dormant until the emailed link is clicked.
        // Show a "check your inbox" screen rather than sending them to a
        // login form that would just reject them.
        setRegisteredEmail(response.data.email || formData.email)
        setEmailSent(response.data.email_sent !== false)
        setStep(5)
      } else {
        toast.success('Registration successful! Please login.')
        navigate('/login')
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errors = error.response.data
        if (errors.email) toast.error(`Email: ${errors.email[0]}`)
        else if (errors.username) toast.error(`Username: ${errors.username[0]}`)
        else if (errors.password) toast.error(`Password: ${errors.password[0]}`)
        else if (errors.error) toast.error(errors.error)
        else toast.error('Registration failed. Please try again.')
      } else {
        toast.error('Registration failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    const clientId = '1018587300192-m0n93uesm6v33bahs57tatg52v3lurah.apps.googleusercontent.com'
    // The app claims /auth/app-callback and only that; sending it to the
    // website's path leaves the sign-up stranded in a browser tab. Same split
    // as Login — see components/deepLinkReturn.test.tsx.
    const callbackPath = isOurApp() ? '/auth/app-callback' : '/auth/callback'
    const redirectUri = encodeURIComponent(`${window.location.origin}${callbackPath}`)
    const scope = encodeURIComponent('openid email profile')
    const state = btoa(JSON.stringify({ mode: 'signup' }))
    // `select_account`, or Google silently reuses whoever is signed in to the
    // browser and picking the wrong account once has no way back.
    const prompt = encodeURIComponent('select_account consent')
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=${prompt}&state=${state}`

    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: authUrl })
    } else {
      window.location.href = authUrl
    }
  }

  return (
    // `relative`, so the glow below anchors to this wrapper on purpose rather
    // than falling through to the viewport because nothing else was positioned.
    <div className="relative min-h-screen bg-neutral-950 flex items-center justify-center p-4">
      {/* Single restrained purple glow — no multi-blob slop */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-purple-600/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="relative overflow-hidden rounded-2xl border border-neutral-800
          bg-neutral-900/70 backdrop-blur-sm p-6 sm:p-8 shadow-card">
          {/* §16's hairline — the house signature on a header surface. */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r
            from-transparent via-purple-500/60 to-transparent" aria-hidden="true" />

          {/* Logo & Title */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <img src="/logo/ccis-logo.png" alt="CCIS CodeHub" className="h-12 w-12 sm:h-14 sm:w-14" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-white">
              {step === 4 ? 'Welcome to CCIS CodeHub' : step === 5 ? 'Check your inbox' : 'Create your account'}
            </h1>
            {step < 4 && (
              // Says which step and what it is. Three bare circles counted
              // without ever saying what they were counting.
              <p className="mt-1 text-xs text-neutral-500">
                Step {step} of 3 · {STEP_NAMES[step - 1]}
              </p>
            )}
          </div>

          {/* Progress Steps */}
          {step < 4 && (
            <div className="flex items-center justify-center mb-6" aria-hidden="true">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  {/* One accent, hierarchy by weight: filled for done, ringed
                      for current, hairline for still to come. Green was doing
                      the work of a third colour on a single axis. */}
                  <div
                    className={`grid h-8 w-8 place-items-center rounded-full border
                      text-xs font-semibold transition ${s === step
                      ? 'border-purple-500 bg-purple-600 text-white ring-2 ring-purple-500/30'
                      : s < step
                        ? 'border-purple-500/40 bg-purple-500/15 text-purple-300'
                        : 'border-neutral-700 text-neutral-500'
                      }`}
                  >
                    {s < step ? <Check className="h-4 w-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`h-px w-8 sm:w-12 transition ${
                      s < step ? 'bg-purple-500/40' : 'bg-neutral-800'}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Name & Username */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-neutral-300">First name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className={FIELD}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-neutral-300">Last name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className={FIELD}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-300">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className={`${FIELD} pl-10`}
                    placeholder="johndoe"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Email, Program, Year */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-300">
                  Institutional email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`${FIELD} pl-10`}
                    placeholder="you@ssct.edu.ph"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-medium text-neutral-300">Program</label>
                <div className="space-y-2">
                  {PROGRAMS.map((program) => {
                    const Icon = program.icon
                    return (
                      <button
                        key={program.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, program: program.value })}
                        aria-pressed={formData.program === program.value}
                        className={`flex w-full items-center gap-3 rounded-lg border
                          p-3 text-left transition focus-visible:outline-none
                          focus-visible:ring-2 focus-visible:ring-purple-500/50 ${
                          formData.program === program.value
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-neutral-700 bg-neutral-900 hover:border-neutral-600'
                          }`}
                      >
                        <div className={`rounded-lg p-2 ${formData.program === program.value ? 'bg-purple-500/15' : 'bg-neutral-800'}`}>
                          <Icon className={`w-4 h-4 ${formData.program === program.value ? 'text-purple-400' : 'text-neutral-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white">{program.label}</div>
                          <div className="text-xs text-neutral-400 truncate">{program.description}</div>
                        </div>
                        {formData.program === program.value && (
                          <Check className="w-4 h-4 text-purple-400 shrink-0" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-medium text-neutral-300">Year level</label>
                <div className="grid grid-cols-4 gap-2">
                  {YEAR_LEVELS.map((year) => {
                    const picked = formData.year_level === year.value
                    return (
                      <button
                        key={year.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, year_level: year.value })}
                        aria-pressed={picked}
                        aria-label={year.label}
                        className={`min-h-11 rounded-lg border py-2.5 text-center transition
                          focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-purple-500/50 ${picked
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-neutral-700 bg-neutral-900 hover:border-neutral-600'}`}
                      >
                        <span className={`block text-base font-semibold tabular-nums ${
                          picked ? 'text-purple-300' : 'text-neutral-200'}`}>
                          {year.ordinal}
                        </span>
                        <span className="mt-0.5 block text-[10px] uppercase
                          tracking-wide text-neutral-500">
                          Year
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-300">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${FIELD} pl-10 pr-10`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-300">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`${FIELD} pl-10 pr-10`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* The two rules `validateStep` already enforces, shown as they are
                  met rather than only announced as a toast after Create is
                  pressed. Purely a readout — the rules themselves are unchanged. */}
              {(formData.password || formData.confirmPassword) && (
                <ul className="space-y-1.5">
                  {[
                    { ok: formData.password.length >= 6, label: 'At least 6 characters' },
                    {
                      ok: Boolean(formData.confirmPassword)
                        && formData.password === formData.confirmPassword,
                      label: 'Both entries match',
                    },
                  ].map(rule => (
                    <li key={rule.label}
                      className={`flex items-center gap-2 text-xs transition ${
                        rule.ok ? 'text-green-400' : 'text-neutral-500'}`}>
                      {/* Icon as well as colour — colour alone must not carry
                          the meaning (§13). */}
                      {rule.ok
                        ? <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
                        : <X className="h-3 w-3 shrink-0" aria-hidden="true" />}
                      {rule.label}
                    </li>
                  ))}
                </ul>
              )}

              {/* CAPTCHA */}
              <CaptchaCheckbox
                onVerified={handleCaptchaVerified}
                onExpired={handleCaptchaExpired}
              />
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Account Created!</h2>
              <p className="text-sm text-neutral-400 mb-4">Redirecting you to the learning platform...</p>
              <Loader2 className="w-5 h-5 mx-auto text-purple-400 animate-spin" />
            </div>
          )}

          {/* Step 5 — account created, waiting on email confirmation */}
          {step === 5 && (
            <div className="text-center py-4">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${emailSent ? 'bg-purple-500/15' : 'bg-amber-500/15'}`}>
                {emailSent
                  ? <MailCheck className="w-8 h-8 text-purple-400" />
                  : <MailWarning className="w-8 h-8 text-amber-400" />}
              </div>

              <h2 className="text-lg font-semibold text-white mb-2">
                {emailSent ? 'Confirm your email' : 'Almost there'}
              </h2>

              {emailSent ? (
                <p className="text-sm text-neutral-400 mb-1">
                  We sent a confirmation link to
                </p>
              ) : (
                <p className="text-sm text-neutral-400 mb-1">
                  Your account was created, but we couldn't send the email to
                </p>
              )}
              <p className="text-sm font-medium text-purple-300 break-all mb-4">{registeredEmail}</p>

              <p className="text-xs text-neutral-500 leading-relaxed mb-6">
                {emailSent
                  ? "Click the link in that email to activate your account, then sign in to finish setting up your profile. Check your spam folder if it hasn't arrived."
                  : 'Request a new link below to activate your account.'}
              </p>

              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="inline-flex min-h-11 w-full items-center justify-center
                  rounded-lg bg-purple-600 text-sm font-semibold text-white
                  transition hover:bg-purple-500 disabled:opacity-50"
              >
                {resending ? 'Sending…' : 'Resend confirmation email'}
              </button>
              <Link
                to="/login"
                className="mt-2 inline-flex min-h-11 w-full items-center
                  justify-center rounded-lg border border-neutral-700 text-sm
                  font-medium text-neutral-300 transition hover:border-neutral-600
                  hover:text-white"
              >
                Go to sign in
              </Link>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex gap-3 mt-6">
              {step > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2
                    rounded-lg border border-neutral-700 text-sm font-medium
                    text-neutral-300 transition hover:border-neutral-600
                    hover:text-white focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-purple-500/50"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2
                    rounded-lg bg-purple-600 text-sm font-semibold text-white
                    transition hover:bg-purple-500 focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-purple-500/50"
                >
                  Continue
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !captchaToken}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2
                    rounded-lg bg-purple-600 text-sm font-semibold text-white
                    transition hover:bg-purple-500 focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-purple-500/50
                    disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Creating account…
                    </>
                  ) : (
                    <>
                      Create account
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Divider - Only on step 1 */}
          {step === 1 && (
            <>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-700/50" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 text-xs text-neutral-500 bg-neutral-900/60">or continue with</span>
                </div>
              </div>

              {/* Google Button - Circle Icon */}
              <div className="flex justify-center">
                <button
                  onClick={handleGoogleSignup}
                  className="w-12 h-12 bg-neutral-100 hover:bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 ring-1 ring-gray-200"
                  title="Sign up with Google"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {/* Footer Links */}
          {step < 4 && (
            <div className="mt-5 text-center space-y-2">
              <p className="text-sm text-neutral-400">
                Already have an account?{' '}
                <Link to="/login" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                  Sign in
                </Link>
              </p>
              <Link to="/" className="inline-flex min-h-11 items-center gap-1.5 px-2 text-xs text-neutral-500 transition-colors hover:text-neutral-300">
                <ArrowLeft className="w-3 h-3" />
                Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
